import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';
import { fileUrl } from '../lib/fileUrl';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductApprovals = () => {
  const [pendingProducts, setPendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();

  const canApprove = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);

  useEffect(() => {
    if (canApprove) {
      fetchPendingProducts();
    } else {
      setLoading(false);
    }
  }, [user, canApprove]);

  const fetchPendingProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/pending`);
      setPendingProducts(response.data);
    } catch (error) {
      console.error('Error fetching pending products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/approve`);
      notify('Product approved successfully');
      fetchPendingProducts();
    } catch (error) {
      console.error('Error approving product:', error);
      notify('Error approving product');
    }
  };

  const handleReject = async (productId) => {
    const ok = await confirm({
      title: 'Reject product?',
      message: 'Are you sure you want to reject this product? This will permanently delete it.',
      confirmText: 'Reject',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/products/${productId}/reject`);
      notify('Product rejected');
      fetchPendingProducts();
    } catch (error) {
      console.error('Error rejecting product:', error);
      notify('Error rejecting product');
    }
  };

  if (!canApprove) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="medical-card p-12 text-center">
          <h2 className="text-2xl font-bold text-[#052A23] mb-2">Access Denied</h2>
          <p className="text-[#6D8E87]">Only National Sales Managers, VPs, and Presidents can approve products</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="product-approvals-page">
      <div className="mb-8">
        <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="page-title">
          Product Approvals
        </h1>
        <p className="text-[#6D8E87]">Review and approve pending product submissions</p>
      </div>

      {pendingProducts.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="no-pending-products">
          <div className="w-16 h-16 bg-[#E2F3F0] rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-[#00a69c]" weight="bold" />
          </div>
          <h3 className="text-xl font-semibold text-[#052A23] mb-2">All Caught Up!</h3>
          <p className="text-[#6D8E87]">No pending product approvals</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pending-products-grid">
          {pendingProducts.map((product) => (
            <div key={product.id} className="medical-card p-6" data-testid={`product-${product.id}`}>
              {/* Product Image Placeholder */}
              <div className="h-40 bg-[#E2F3F0] rounded-lg mb-4 flex items-center justify-center">
                {product.images.length > 0 ? (
                  <img
                    src={fileUrl(product.images[0])}
                    alt={product.name}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <img
                    src="https://images.unsplash.com/photo-1641877492086-771bc14a8e5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGZ1cm5pdHVyZXxlbnwwfHx8fDE3NzU3MTczNTl8MA&ixlib=rb-4.1.0&q=85"
                    alt={product.name}
                    className="w-full h-full object-cover rounded-lg opacity-50"
                  />
                )}
              </div>

              {/* Product Info */}
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-[#FEF3C7] text-[#92400E] text-xs rounded-full mb-2">
                  PENDING APPROVAL
                </span>
                <h3 className="text-xl font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>
                  {product.name}
                </h3>
                <p className="text-xs text-[#6D8E87] mb-2">{product.category}</p>
                <p className="text-sm text-[#3B5A54] mb-3 line-clamp-2">{product.description}</p>
                <div className="text-2xl font-bold text-[#00a69c] mb-2" style={{ fontFamily: 'Outfit' }}>
                  ₹{product.price.toLocaleString('en-IN')}
                </div>
              </div>

              {/* Created By Info */}
              <div className="border-t border-[#D5E8E4] pt-3 mb-4">
                <p className="text-xs text-[#6D8E87]">Submitted by</p>
                <p className="text-sm font-medium text-[#3B5A54]">{product.created_by_name}</p>
                <p className="text-xs text-[#6D8E87]">{new Date(product.created_at).toLocaleDateString()}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleReject(product.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-[#EF4444] rounded-lg hover:bg-red-100 transition"
                  data-testid={`reject-${product.id}`}
                >
                  <X size={20} weight="bold" />
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(product.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#00bfb3] transition"
                  data-testid={`approve-${product.id}`}
                >
                  <Check size={20} weight="bold" />
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductApprovals;
